import type { SubMarket } from "../api";
import type { ReactNode } from "react";
import { ALL_FRAME_TYPES } from "../hooks/useSharedParams";
import { formatCountry } from "../utils/countries";
import { buildClosedTertials, periodDateRange, periodStartDate } from "../utils/dates";
import styles from "./FilterBar.module.css";
import { DateRangeModal } from "./DateRangeModal";
import { MultiSelect } from "./MultiSelect";
import { SingleSelect } from "./SingleSelect";
import { useMemo, useState } from "react";

// All available sales channels
export const ALL_SALES_CHANNELS = ["Store", "Online", "Remote"];

interface FilterBarProps {
    selectedSubMarkets: string[];
    countries: string[];
    selectedCountries: string[];
    salesChannels: string[];
    selectedSalesChannels: string[];
    period: string;
    subMarkets: SubMarket[];
    favoriteCountry?: string;
    favoritePeriod?: string;
    frameTypes?: string[];
    /** Available frame type categories for the current anchor */
    frameTypeOptions?: string[];
    /** Default frame types for the current anchor (subset of frameTypeOptions) */
    defaultFrameTypes?: string[];
    /** Earliest date with data for current anchor (ISO string or undefined) */
    dataAvailableFrom?: string;
    onSubMarketsChange: (subMarkets: string[]) => void;
    onCountriesChange: (countries: string[]) => void;
    onSalesChannelsChange: (channels: string[]) => void;
    onPeriodChange: (period: string) => void;
    onFrameTypesChange?: (types: string[]) => void;
    onFavoriteCountryChange?: (country: string | null) => void;
    onFavoritePeriodChange?: (period: string | null) => void;
    /** Cross-filtered receipt counts per dimension */
    countryCounts?: Record<string, number>;
    subMarketCounts?: Record<string, number>;
    channelCounts?: Record<string, number>;
    frameTypeCounts?: Record<string, number>;
    /** Whether counts are being re-fetched */
    countsLoading?: boolean;
    /** Optional control rendered as the first item in the bar (e.g. anchor selector) */
    leading?: ReactNode;
}

// IKEA fiscal year starts Sep 1 — FY26 = Sep 2025 – Aug 2026
const now = new Date();
const fyYear = (now.getMonth() >= 8 ? now.getFullYear() + 1 : now.getFullYear()) % 100;

interface PeriodOption {
    value: string;
    label: string;
    tooltip?: string;
    disabled?: boolean;
    disabledTooltip?: string;
}

function buildPeriods(dataAvailableFrom?: string): PeriodOption[] {
    const tertials = buildClosedTertials();
    const periods: PeriodOption[] = [
        { value: "1w", label: "Last week" },
        { value: "4w", label: "4 weeks" },
        { value: "8w", label: "8 weeks" },
        { value: "12w", label: "12 weeks" },
        { value: "16w", label: "16 weeks" },
        { value: "52w", label: "52 weeks" },
        ...tertials,
        { value: "fy", label: `Year to date`, tooltip: `Year to date — fiscal year FY${fyYear} so far (Sep 1, ${2000 + fyYear - 1} – yesterday)` },
        { value: "custom", label: "Custom…", tooltip: `Custom date interval — currently limited to FY${fyYear} (from Sep 1, 2025)` },
    ];
    return periods.map(p => {
        // Check data_available_from date constraint
        if (dataAvailableFrom && p.value !== "custom") {
            const startDate = periodStartDate(p.value);
            if (startDate && startDate < dataAvailableFrom) {
                return { ...p, disabled: true, disabledTooltip: `Data not available before ${dataAvailableFrom}` };
            }
        }
        if (!p.tooltip) {
            const range = periodDateRange(p.value);
            if (range) return { ...p, tooltip: `Closed weeks: ${range}` };
        }
        return p;
    });
}

const FRAME_TYPE_LABELS: Record<string, string> = {
    regular: "Regular",
    corner: "Corner",
    end: "End unit",
    mid: "Mid unit",
    height_extension: "Height extension",
    tv: "TV bench",
};

const FRAME_TYPE_TOOLTIPS: Record<string, string> = {
    regular: "Standard frames (wardrobes, storage units)",
    corner: "Corner units placed at 90° between two frame runs",
    end: "End units placed at 90° at the end of a frame run, covering the side",
    mid: "Narrow frames placed between two wardrobe frames",
    height_extension: "Stacked height extensions; excluded from horizontal width and base-frame height",
    tv: "TV benches and TV storage units",
};

export function FilterBar({
    selectedSubMarkets,
    countries,
    selectedCountries,
    salesChannels,
    selectedSalesChannels,
    period,
    subMarkets,
    favoriteCountry,
    favoritePeriod,
    frameTypes,
    frameTypeOptions,
    defaultFrameTypes,
    dataAvailableFrom,
    onSubMarketsChange,
    onCountriesChange,
    onSalesChannelsChange,
    onPeriodChange,
    onFrameTypesChange,
    onFavoriteCountryChange,
    onFavoritePeriodChange,
    countryCounts,
    subMarketCounts,
    channelCounts,
    frameTypeCounts,
    countsLoading,
    leading,
}: FilterBarProps) {
    const [showDateModal, setShowDateModal] = useState(false);

    // Compute available periods based on data availability config
    const periods = useMemo(
        () => buildPeriods(dataAvailableFrom),
        [dataAvailableFrom],
    );

    // Build lookup for sub market names
    const subMarketMap = new Map(subMarkets.map(s => [s.code, s]));
    const subMarketOptions = subMarkets.map(s => s.code);


    // Format sub market option for display in dropdown
    const formatSubMarketOption = (code: string) => {
        const sm = subMarketMap.get(code);
        return sm?.name ? `${code} - ${sm.name}` : code;
    };

    // Parse custom period for initial modal values
    const isCustom = period.startsWith("custom:");
    const customDates = isCustom ? period.split(":").slice(1) : undefined;

    // Display value: show "Custom" label for custom periods, otherwise the preset
    const displayPeriod = isCustom ? "custom" : period;

    const handlePeriodSelect = (value: string) => {
        if (value === "custom") {
            setShowDateModal(true);
        } else {
            onPeriodChange(value);
        }
    };

    return (
        <div className={styles.filterBar}>
            {leading}
            <MultiSelect
                label="Retail Unit"
                options={countries}
                selected={selectedCountries}
                onChange={onCountriesChange}
                placeholder="Select retail units..."
                allSelectedText="All retail units"
                favorite={favoriteCountry}
                onFavoriteChange={onFavoriteCountryChange}
                showSelectedSummary={true}
                formatOption={formatCountry}
                tooltip="Select one or more retail units to include in the analysis"
                counts={countryCounts}
                countsLoading={countsLoading}
            />

            <MultiSelect
                label="Sales Channel"
                options={salesChannels}
                selected={selectedSalesChannels}
                onChange={onSalesChannelsChange}
                placeholder="Select channels..."
                allSelectedText="All channels"
                showSelectedSummary={true}
                tooltip="Filter by sales channel: Store (physical), Online, or Remote"
                counts={channelCounts}
                countsLoading={countsLoading}
            />

            <MultiSelect
                label="Sub-market"
                options={subMarketOptions}
                selected={selectedSubMarkets}
                onChange={onSubMarketsChange}
                placeholder="Select sub-markets..."
                allSelectedText="All sub-markets"
                formatOption={formatSubMarketOption}
                showSelectedSummary={true}
                formatSummaryItem={(code) => code}
                tooltip="Filter by sub-market"
                counts={subMarketCounts}
                countsLoading={countsLoading}
                disabledMessage={selectedCountries.length === 0 ? "Select a retail unit first" : undefined}
            />

            <SingleSelect
                label="Period"
                options={periods}
                value={displayPeriod}
                onChange={handlePeriodSelect}
                subtitle={periodDateRange(period)}
                tooltip="Time window for aggregating sales data"
                favorite={favoritePeriod}
                onFavoriteChange={onFavoritePeriodChange}
            />

            {showDateModal && (
                <DateRangeModal
                    initialStart={customDates?.[0]}
                    initialEnd={customDates?.[1]}
                    minAllowedDate={dataAvailableFrom}
                    onCancel={() => setShowDateModal(false)}
                    onApply={(start, end) => {
                        setShowDateModal(false);
                        onPeriodChange(`custom:${start}:${end}`);
                    }}
                />
            )}

            {onFrameTypesChange && (
                <MultiSelect
                    label="Frame Type"
                    options={frameTypeOptions || ALL_FRAME_TYPES}
                    selected={frameTypes || frameTypeOptions || ALL_FRAME_TYPES}
                    onChange={onFrameTypesChange}
                    placeholder="Select frame types..."
                    allSelectedText="All frame types"
                    defaultSelected={defaultFrameTypes}
                    formatOption={(t) => FRAME_TYPE_LABELS[t] || t}
                    tooltip="Filter combinations by frame type"
                    optionTooltips={FRAME_TYPE_TOOLTIPS}
                    counts={frameTypeCounts}
                    countsLoading={countsLoading}
                />
            )}
        </div>
    );
}
